import { useField } from 'formik';
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  SelectProps,
  FormHelperText,
  InputLabelProps,
  CircularProgress,
} from '@mui/material';

import { controlError } from 'utils/error';

export interface SelectFieldProps extends Omit<SelectProps, 'name'> {
  name: string;
  label?: string;
  loading?: boolean;
  noDataLabel?: string;
  controlErrorLabel?: boolean;
  labelProps?: InputLabelProps;
  onChange?: SelectProps['onChange'];
  options: { label: string; value: string | number }[];
}

export const SelectField = ({
  name,
  label,
  options,
  loading,
  onChange,
  labelProps,
  noDataLabel = 'No data',
  ...props
}: SelectFieldProps) => {
  const [field, meta] = useField(name);

  const errorText = controlError(meta, name, label);
  const hasError = Boolean(errorText);
  const labelId = `${name}-label`;

  return (
    <FormControl fullWidth error={hasError}>
      {label && (
        <InputLabel id={labelId} {...labelProps}>
          {label}
        </InputLabel>
      )}
      <Select
        {...field}
        {...props}
        label={label}
        error={hasError}
        labelId={labelId}
        disabled={loading || props.disabled}
        onChange={(event, child) => {
          field.onChange(event);
          onChange?.(event, child);
        }}
        IconComponent={
          loading
            ? () => (
                <CircularProgress
                  size={20}
                  sx={{ right: 10, position: 'absolute' }}
                />
              )
            : undefined
        }
      >
        {options.length === 0 ? (
          <MenuItem disabled value="">
            {noDataLabel}
          </MenuItem>
        ) : (
          options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))
        )}
      </Select>
      {hasError && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};
